import { useEffect, useState } from 'react';
import { Table, Input, Select, DatePicker, Button, Tag, Space, App, Modal, Descriptions } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Download, Filter, RefreshCw, Eye } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { apiClient } from '@/services/apiClient';
import { PageHeader, Card } from '@/components/common/PageHeader';

interface AuditLog {
  id: number;
  timestamp: string;
  userId?: string | null;
  username?: string | null;
  ip?: string | null;
  action: string;
  entityType?: string | null;
  entityId?: string | null;
  entityDescription?: string | null;
  result: 'SUCCESS' | 'FAILURE';
  failureReason?: string | null;
}

interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

const ACTION_COLORS: Record<string, string> = {
  LOGIN: 'blue', LOGOUT: 'default',
  CREATE_USER: 'green', UPDATE_USER: 'cyan', DELETE_USER: 'red',
  RESET_PASSWORD: 'orange', ASSIGN_ROLES: 'purple',
  SET_STATUS_LOCKED: 'red', SET_STATUS_ACTIVE: 'green',
  CREATE_TASK: 'green', UPDATE_TASK: 'cyan', DELETE_TASK: 'red',
  SIGN_DOC: 'magenta', REVOKE_SESSION: 'orange',
  CREATE_CATEGORY: 'green', UPDATE_CATEGORY: 'cyan', DELETE_CATEGORY: 'red',
  CREATE_ROLE: 'green', UPDATE_ROLE: 'cyan', DELETE_ROLE: 'red',
  UPDATE_ROLE_PERMISSIONS: 'purple',
};

const ACTION_OPTIONS = [
  'LOGIN', 'CREATE_USER', 'UPDATE_USER', 'SET_STATUS_LOCKED', 'SET_STATUS_ACTIVE',
  'RESET_PASSWORD', 'ASSIGN_ROLES', 'REVOKE_SESSION',
  'CREATE_TASK', 'UPDATE_TASK', 'SIGN_DOC',
  'CREATE_CATEGORY', 'UPDATE_CATEGORY', 'DELETE_CATEGORY',
  'CREATE_ROLE', 'UPDATE_ROLE', 'DELETE_ROLE', 'UPDATE_ROLE_PERMISSIONS',
];

// V1.0.65: Thêm Project + Document vào filter audit
const ENTITY_OPTIONS = ['Auth', 'User', 'UserSession', 'Task', 'Project', 'Document', 'Signature', 'Category', 'Role'];

export function AuditLogPage() {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filters
  const [username, setUsername] = useState('');
  const [action, setAction] = useState<string | undefined>();
  const [entityType, setEntityType] = useState<string | undefined>();
  const [result, setResult] = useState<string | undefined>();
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);

  // Detail modal
  const [detail, setDetail] = useState<AuditLog | null>(null);

  const buildParams = () => {
    const params: any = { page, pageSize };
    if (username) params.username = username;
    if (action) params.action = action;
    if (entityType) params.entityType = entityType;
    if (result) params.result = result;
    if (fromDate) params.fromDate = fromDate.toISOString();
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<Paginated<AuditLog>>('/admin/audit-logs', { params: buildParams() });
      setData(data.items);
      setTotal(data.total);
    } catch (e: any) {
      message.error('Không tải được nhật ký: ' + (e.response?.data?.message ?? e.message));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, pageSize]);

  const applyFilter = () => {
    setPage(1);
    load();
  };

  const clearFilter = () => {
    setUsername(''); setAction(undefined); setEntityType(undefined);
    setResult(undefined); setFromDate(null);
    setPage(1);
    setTimeout(load, 0);
  };

  const exportCsv = async () => {
    try {
      const params = { ...buildParams() };
      delete params.page;
      delete params.pageSize;
      const response = await apiClient.get('/admin/audit-logs/export', {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${dayjs().format('YYYYMMDD-HHmmss')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('Đã xuất CSV');
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    }
  };

  const columns: ColumnsType<AuditLog> = [
    {
      title: 'Thời điểm', dataIndex: 'timestamp', width: 160,
      render: (t: string) => <span className="mono" style={{ fontSize: 11 }}>{new Date(t).toLocaleString('vi-VN', { hour12: false })}</span>,
    },
    {
      title: 'User', dataIndex: 'username', width: 130,
      render: (v: string) => v ? <span className="mono" style={{ fontSize: 11 }}>{v}</span> : <span style={{ color: '#adb6c0' }}>system</span>,
    },
    {
      title: 'IP', dataIndex: 'ip', width: 110,
      render: (v: string) => v ? <span className="mono" style={{ fontSize: 11, color: '#5b6875' }}>{v}</span> : '-',
    },
    {
      title: 'Hành động', dataIndex: 'action', width: 180,
      render: (a: string) => <Tag color={ACTION_COLORS[a] || 'default'} style={{ fontSize: 10 }}>{a}</Tag>,
    },
    {
      title: 'Đối tượng', width: 220,
      render: (_: any, log: AuditLog) => (
        <div>
          <div style={{ fontSize: 11, color: '#5b6875' }}>{log.entityType ?? '-'}</div>
          <div style={{ fontSize: 11 }}>{log.entityDescription ?? log.entityId ?? ''}</div>
        </div>
      ),
    },
    {
      title: 'Kết quả', dataIndex: 'result', width: 100,
      render: (r: string) => r === 'SUCCESS' ? <Tag color="success">✓ Thành công</Tag> : <Tag color="error">✗ Lỗi</Tag>,
    },
    {
      title: '', width: 60, fixed: 'right' as const,
      render: (_: any, log: AuditLog) => (
        <Button type="text" icon={<Eye size={14} />} onClick={() => setDetail(log)} />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Nhật ký hệ thống"
        subtitle={`${total.toLocaleString('vi-VN')} bản ghi · Append-only · TT54 compliant`}
        extra={
          <>
            <Button icon={<Download size={14} />} onClick={exportCsv}>Export CSV</Button>
            <Button icon={<RefreshCw size={14} />} onClick={load}>Tải lại</Button>
          </>
        }
      />

      <Card padding={0}>
        <div style={{ padding: 16, borderBottom: '1px solid #eceef1', background: '#f6f7f8' }}>
          <Space wrap>
            <Input placeholder="Username..." value={username} style={{ width: 180 }}
              onChange={e => setUsername(e.target.value)} onPressEnter={applyFilter} allowClear />
            <Select placeholder="Hành động" style={{ width: 200 }} allowClear showSearch
              value={action} onChange={setAction}
              options={ACTION_OPTIONS.map(a => ({ value: a, label: a }))} />
            <Select placeholder="Loại đối tượng" style={{ width: 150 }} allowClear
              value={entityType} onChange={setEntityType}
              options={ENTITY_OPTIONS.map(t => ({ value: t, label: t }))} />
            <Select placeholder="Kết quả" style={{ width: 130 }} allowClear
              value={result} onChange={setResult}
              options={[
                { value: 'SUCCESS', label: '✓ Success' },
                { value: 'FAILURE', label: '✗ Failure' },
              ]} />
            <DatePicker placeholder="Từ ngày" value={fromDate} onChange={setFromDate}
              showTime format="DD/MM/YYYY HH:mm" style={{ width: 180 }} />
            <Button type="primary" icon={<Filter size={14} />} onClick={applyFilter}>Lọc</Button>
            <Button onClick={clearFilter}>Xóa lọc</Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          size="middle"
          scroll={{ x: 1000 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100, 200],
            showTotal: (t) => `${t.toLocaleString('vi-VN')} bản ghi`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
        />
      </Card>

      <Modal
        title="Chi tiết nhật ký"
        open={!!detail}
        onCancel={() => setDetail(null)}
        footer={null}
        width={720}
      >
        {detail && (
          <Descriptions column={1} bordered size="small" styles={{ label: { width: 160 } }}>
            <Descriptions.Item label="ID">
              <span className="mono">{detail.id}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Thời điểm">
              <span className="mono">{new Date(detail.timestamp).toLocaleString('vi-VN', { hour12: false })}</span>
            </Descriptions.Item>
            <Descriptions.Item label="User">
              {detail.username ? <span className="mono">{detail.username}</span> : <i>system</i>}
            </Descriptions.Item>
            <Descriptions.Item label="User ID">
              <span className="mono" style={{ fontSize: 11 }}>{detail.userId ?? '-'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="IP">
              <span className="mono">{detail.ip ?? '-'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Hành động">
              <Tag color={ACTION_COLORS[detail.action] || 'default'}>{detail.action}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Loại đối tượng">
              {detail.entityType ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Entity ID">
              <span className="mono" style={{ fontSize: 11 }}>{detail.entityId ?? '-'}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Mô tả">
              {detail.entityDescription ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Kết quả">
              {detail.result === 'SUCCESS' ? <Tag color="success">✓ Thành công</Tag> : <Tag color="error">✗ Lỗi</Tag>}
            </Descriptions.Item>
            {detail.failureReason && (
              <Descriptions.Item label="Lý do lỗi">
                <span style={{ color: '#b91c1c' }}>{detail.failureReason}</span>
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </>
  );
}
